/**
 * Minimal RFC 4180-style CSV serialization/parsing for the bulk-import manifest.
 *
 * Domain-free on purpose: it converts between rows of string-keyed records and
 * CSV text. The bulk-import hook maps these records to/from typed bindings,
 * resolving names against the loaded option lists. Kept dependency-free (no
 * papaparse) since the manifest shape is simple and bounded.
 */

/**
 * Quotes a single CSV cell when it contains a comma, quote, or newline, escaping
 * embedded quotes by doubling them.
 *
 * @param value The raw cell value.
 * @returns The cell, quoted only if required.
 */
const quoteCell = (value: string): string => {
  if (/[",\n\r]/.test(value)) {
    return `"${value.replaceAll('"', '""')}"`;
  }
  return value;
};

/**
 * Serializes records to CSV text using a fixed column order. Missing keys render
 * as empty cells.
 *
 * @param records The rows to serialize (string-keyed).
 * @param columns The ordered column headers to emit.
 * @returns CSV text with a header line followed by one line per record.
 */
export const toCsv = (
  records: Array<Record<string, string>>,
  columns: readonly string[],
): string => {
  const header = columns.map((column) => quoteCell(column)).join(",");
  const lines = records.map((record) =>
    columns.map((column) => quoteCell(record[column] ?? "")).join(","),
  );
  return [header, ...lines].join("\n");
};

/**
 * Parses a single CSV line into cells, honoring quoted fields with embedded
 * commas, newlines (within the already-joined field), and doubled quotes.
 *
 * @param line A single physical CSV record (quotes already balanced).
 * @returns The parsed cells.
 */
const parseLine = (line: string): string[] => {
  const cells: string[] = [];
  let current = "";
  let inQuotes = false;
  for (let i = 0; i < line.length; i += 1) {
    const char = line[i];
    if (inQuotes) {
      if (char === '"') {
        if (line[i + 1] === '"') {
          current += '"';
          i += 1;
        } else {
          inQuotes = false;
        }
      } else {
        current += char;
      }
    } else if (char === '"') {
      inQuotes = true;
    } else if (char === ",") {
      cells.push(current);
      current = "";
    } else {
      current += char;
    }
  }
  cells.push(current);
  return cells;
};

/**
 * Splits CSV text into physical records, treating newlines inside quoted fields
 * as part of the field rather than record separators.
 *
 * @param csv The full CSV text.
 * @returns One string per logical record (header included).
 */
const splitRecords = (csv: string): string[] => {
  const records: string[] = [];
  let current = "";
  let inQuotes = false;
  for (let i = 0; i < csv.length; i += 1) {
    const char = csv[i];
    if (char === '"') {
      inQuotes = !inQuotes;
      current += char;
    } else if ((char === "\n" || char === "\r") && !inQuotes) {
      if (char === "\r" && csv[i + 1] === "\n") {
        i += 1;
      }
      records.push(current);
      current = "";
    } else {
      current += char;
    }
  }
  if (current.length > 0) {
    records.push(current);
  }
  return records;
};

/**
 * Parses CSV text into string-keyed records using the first line as headers.
 * Blank lines are skipped. Cells beyond the header count are ignored; missing
 * trailing cells become empty strings.
 *
 * @param csv The CSV text (with a header line).
 * @returns One record per data line, keyed by header name.
 */
export const fromCsv = (csv: string): Array<Record<string, string>> => {
  const lines = splitRecords(csv).filter((line) => line.trim().length > 0);
  if (lines.length === 0) {
    return [];
  }
  const headers = parseLine(lines[0] ?? "");
  return lines.slice(1).map((line) => {
    const cells = parseLine(line);
    const record: Record<string, string> = {};
    for (const [index, headerName] of headers.entries()) {
      record[headerName] = (cells[index] ?? "").trim();
    }
    return record;
  });
};
